import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';

import type { WorkTaskAutomation } from '../../../data-contract/task-management';
import { TaskDetailsBox, type TaskMetaRow } from '../tile/TaskFieldPrimitives';
import {
    automationAssessmentTranslationKeys,
    automationRecommendationTranslationKeys as keys,
} from './automation-assessment-translations';

type ComplexityLevel = NonNullable<WorkTaskAutomation['complexityLevel']>;

export const complexityKeys = {
    high: keys.complexityHigh,
    low: keys.complexityLow,
    medium: keys.complexityMedium,
} as const satisfies Record<ComplexityLevel, string>;

/** What the Details block shows of a recommended automation: its fit, share, autonomy, complexity, function and products. */
export type AutomationDetails = Pick<
    WorkTaskAutomation,
    | 'automatablePercentage'
    | 'automationReasoning'
    | 'autonomyLevel'
    | 'businessFunction'
    | 'complexityLevel'
    | 'products'
    | 'score'
    | 'scoreReasoning'
>;

const hasText = (value: string | undefined): value is string => value !== undefined && value.trim().length > 0;

const useAutomationDetailRows = (automation: AutomationDetails): readonly TaskMetaRow[] => {
    const translate = useTranslate();
    const percent = (value: number): string => translate(automationAssessmentTranslationKeys.percent, { value });
    const rows: TaskMetaRow[] = [
        {
            key: 'fit',
            label: translate(keys.fitLabel),
            ...(hasText(automation.scoreReasoning) ? { tooltip: automation.scoreReasoning } : {}),
            value: (
                <span className="text-body-xs tabular-nums text-text-primary">
                    {percent(Math.round(automation.score * 100))}
                </span>
            ),
        },
    ];

    if (automation.automatablePercentage !== undefined) {
        rows.push({
            key: 'automatable',
            label: translate(keys.automatable),
            ...(hasText(automation.automationReasoning) ? { tooltip: automation.automationReasoning } : {}),
            value: (
                <span className="text-body-xs tabular-nums text-text-primary">
                    {percent(automation.automatablePercentage)}
                </span>
            ),
        });
    }

    if (automation.autonomyLevel !== undefined) {
        rows.push({
            key: 'autonomy',
            label: translate(keys.autonomy),
            value: (
                <span className="text-body-xs text-text-primary">
                    {translate(keys.autonomyValue, { value: automation.autonomyLevel })}
                </span>
            ),
        });
    }

    if (automation.complexityLevel !== undefined) {
        rows.push({
            key: 'complexity',
            label: translate(keys.complexity),
            value: <Badge variant="gray">{translate(complexityKeys[automation.complexityLevel])}</Badge>,
        });
    }

    if (hasText(automation.businessFunction)) {
        rows.push({
            key: 'business-function',
            label: translate(keys.businessFunction),
            value: <span className="text-body-xs text-text-primary">{automation.businessFunction}</span>,
        });
    }

    if (automation.products.length > 0) {
        rows.push({
            key: 'products',
            label: translate(keys.products),
            value: (
                <ul className="m-0 flex list-none flex-wrap justify-end gap-xs p-0">
                    {automation.products.map((product) => (
                        <li key={product.productId}>
                            <Badge variant="gray">{product.name}</Badge>
                        </li>
                    ))}
                </ul>
            ),
        });
    }

    return rows;
};

/** The Details block of a recommended automation, shared by the automation recommendation and agent panels. */
export const AutomationDetailsBox: FunctionComponent<{
    readonly details: AutomationDetails;
    readonly testId?: string;
    readonly title?: string;
}> = ({ details, testId, title }) => {
    const rows = useAutomationDetailRows(details);

    return <TaskDetailsBox rows={rows} testId={testId} title={title} />;
};
